import clsx from 'clsx';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { CampaignCadenceCardPanel } from '../../../../features/campaign-cadences/ui/campaign-cadence-card/CampaignCadenceCardPanel';
import { CampaignCadenceStepCard } from '../../../../features/campaign-cadences/ui/campaign-cadence-card/CampaignCadenceStepCard';
import { TriggerCadenceCard } from '../../../../features/campaign-cadences/ui/campaign-cadence-card/TriggerCadenceCard';
import { CAMPAIGN_CADENCES_TYPE_TAB_KEY } from '../../../../features/campaign-cadences/constants/campaignCadencesTabs';
import { useCampaignCadenceStepsDnd } from '../../../../features/campaign-cadences/hooks/useCampaignCadenceStepsDnd';
import {
  moveStep,
  moveTemplateToStep,
} from '../../../../features/campaign-cadences/lib/cadenceStepMoves';
import { EmptyState } from '../../../../shared/ui/empty-state/EmptyState';
import styles from './CampaignCadencesList.module.css';

const EMPTY_STATE = {
  title: 'No templates yet',
  subtitle: 'There are currently no templates available for this campaign.',
  ariaLabel: 'No templates',
};

// Trigger templates keep the plain one-per-card list; nothing here is reorderable.
function TriggerCadencesList({ items, onPreview, onEdit }) {
  return (
    <div className={clsx(styles.list)}>
      {items.map((item) => (
        <div key={item.id} className={clsx(styles.step)}>
          <TriggerCadenceCard
            item={item}
            onPreview={onPreview ? () => onPreview(item.template) : undefined}
            onEdit={onEdit ? () => onEdit(item.template) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

function MarketingCadenceStepsList({
  steps,
  languages,
  isEditing,
  onStepsChange,
  onPreview,
  onEdit,
}) {
  const { sensors, activeSlot, onDragStart, onDragEnd, onDragCancel } = useCampaignCadenceStepsDnd(
    steps,
    onStepsChange,
  );

  const activeItem = activeSlot
    ? (steps[activeSlot.stepIndex]?.itemsByLanguage?.[activeSlot.language] ?? null)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className={clsx(styles.list)}>
        {steps.map((step, index) => (
          <div key={step.key} className={clsx(styles.step)}>
            <p className={clsx(styles.stepLabel)}>Step {index + 1}</p>
            <CampaignCadenceStepCard
              step={step}
              stepIndex={index}
              stepNumber={index + 1}
              totalSteps={steps.length}
              languages={languages}
              isEditing={isEditing}
              onMoveStepUp={() => onStepsChange(moveStep(steps, index, index - 1))}
              onMoveStepDown={() => onStepsChange(moveStep(steps, index, index + 1))}
              onMoveTemplateToStep={(language, targetStep) =>
                onStepsChange(
                  moveTemplateToStep(steps, {
                    fromIndex: index,
                    toIndex: targetStep - 1,
                    language,
                  }),
                )
              }
              onPreview={onPreview}
              onEdit={onEdit}
            />
          </div>
        ))}
      </div>
      <DragOverlay>
        {activeItem ? (
          <CampaignCadenceCardPanel
            template={activeItem.template}
            variant={CAMPAIGN_CADENCES_TYPE_TAB_KEY.MARKETING}
            showLanguageFlag
            isEditing
            appearance="overlay"
            isGripActive
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export function CampaignCadencesList({
  steps = [],
  items = [],
  languages,
  isEditing,
  variant = CAMPAIGN_CADENCES_TYPE_TAB_KEY.MARKETING,
  onStepsChange,
  onPreview,
  onEdit,
}) {
  if (variant === CAMPAIGN_CADENCES_TYPE_TAB_KEY.TRIGGERS) {
    if (items.length === 0) return <EmptyState {...EMPTY_STATE} />;
    return <TriggerCadencesList items={items} onPreview={onPreview} onEdit={onEdit} />;
  }

  if (steps.length === 0) return <EmptyState {...EMPTY_STATE} />;

  return (
    <MarketingCadenceStepsList
      steps={steps}
      languages={languages}
      isEditing={isEditing}
      onStepsChange={onStepsChange}
      onPreview={onPreview}
      onEdit={onEdit}
    />
  );
}
